import { useMemo, useState } from 'react';
import { Alert } from '@aigovops/design-system';
import { SampleShell, DispositionChip } from './SampleShell';
import {
  SUPPLIER_ROWS,
  SUPPLIER_SUMMARY,
  DISPOSITION_LABELS,
  type SupplierDisposition,
  type SupplierRow,
} from '../../data/samples/supplier-register';

/**
 * Public sample of a supplier AI risk register — GAP-1 in docs/v4/link-gaps-plan.md.
 * Purple accent (Buying lane). Read-only, no auth, static seed data.
 */

const DISPOSITIONS: Array<{ key: SupplierDisposition; tone: 'systemic' | 'pending' | 'awaiting' | 'outside' }> = [
  { key: 'under_systemic_oversight', tone: 'systemic' },
  { key: 'pending_review', tone: 'pending' },
  { key: 'awaiting_classification', tone: 'awaiting' },
  { key: 'outside_oversight_scope', tone: 'outside' },
];

const toneForDisposition = (d: SupplierDisposition): 'systemic' | 'pending' | 'awaiting' | 'outside' | 'retired' => {
  switch (d) {
    case 'under_systemic_oversight': return 'systemic';
    case 'pending_review': return 'pending';
    case 'awaiting_classification': return 'awaiting';
    case 'outside_oversight_scope': return 'outside';
    case 'retired': return 'retired';
  }
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toISOString().slice(0, 10);
};

const daysBetween = (iso: string | null): number | null => {
  if (!iso) return null;
  const diff = Date.parse(iso) - Date.now();
  return Math.round(diff / (1000 * 60 * 60 * 24));
};

export function SupplierAiRegisterSample() {
  const [filter, setFilter] = useState<SupplierDisposition | 'all'>('all');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filtered = useMemo(
    () => (filter === 'all' ? SUPPLIER_ROWS : SUPPLIER_ROWS.filter((r) => r.disposition === filter)),
    [filter],
  );

  const selected = SUPPLIER_ROWS.find((r) => r.id === selectedId) ?? null;

  return (
    <SampleShell
      accent="buying"
      eyebrow="SAMPLE · SUPPLIER AI REGISTER"
      title="See which suppliers manage their AI systemically — and which sit outside your oversight scope."
      subtitle="A supplier-by-supplier AI register, continuously refreshed as suppliers attest. Click any row to see the attestation trail, signals, and the named signer."
      laneHref="/buying"
      laneLabel="Buying overview"
      crossLane={{
        href: '/buying/samples/building-ai-register',
        label: 'Or see the builder\'s view — the AI you deploy',
      }}
    >
      {/* Summary tiles */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}
      >
        <SummaryTile
          label="Suppliers in register"
          value={SUPPLIER_SUMMARY.totalSuppliers}
          caption="Refreshed continuously — no re-survey each cycle"
        />
        <SummaryTile
          label="Under systemic oversight"
          value={SUPPLIER_SUMMARY.underSystemicOversight}
          caption="Signed attestations current"
          accent
        />
        <SummaryTile
          label="On active remediation SLA"
          value={SUPPLIER_SUMMARY.onActiveRemediationSla}
          caption="Pending review + outside oversight scope"
        />
        <SummaryTile
          label="Undisclosed AI flagged"
          value={SUPPLIER_SUMMARY.undisclosedAi}
          caption="AI use detected in identity logs, not in supplier disclosures"
          alert
        />
      </div>

      {/* Undisclosed-AI callout — the wow moment */}
      {SUPPLIER_SUMMARY.undisclosedAi > 0 ? (
        <div style={{ marginBottom: 'var(--space-6)' }}>
          <Alert variant="info" title={`${SUPPLIER_SUMMARY.undisclosedAi} suppliers show AI use in your identity logs that isn't in their disclosure.`}>
            Correlated from Okta / Entra sign-in events against the supplier attestation record.
            Each flagged supplier has a remediation SLA active.{' '}
            <button
              type="button"
              onClick={() => setFilter('outside_oversight_scope')}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--buying)',
                cursor: 'pointer',
                fontFamily: 'var(--font-ui)',
                fontWeight: 600,
                padding: 0,
                textDecoration: 'underline',
                textUnderlineOffset: '3px',
              }}
            >
              Filter to outside oversight scope →
            </button>
          </Alert>
        </div>
      ) : null}

      {/* Disposition filter chips */}
      <div
        role="tablist"
        aria-label="Filter by disposition"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-2)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <FilterChip label="All" count={SUPPLIER_ROWS.length} active={filter === 'all'} onClick={() => setFilter('all')} />
        {DISPOSITIONS.map((d) => {
          const count = SUPPLIER_ROWS.filter((r) => r.disposition === d.key).length;
          return (
            <FilterChip
              key={d.key}
              label={DISPOSITION_LABELS[d.key]}
              count={count}
              active={filter === d.key}
              onClick={() => setFilter(d.key)}
            />
          );
        })}
      </div>

      {/* Table */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          overflow: 'hidden',
        }}
      >
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--surface-elevated)', color: 'var(--text-dim)' }}>
              <TableHead>Supplier</TableHead>
              <TableHead>Disposition</TableHead>
              <TableHead>Risk tier</TableHead>
              <TableHead>AI systems</TableHead>
              <TableHead>Framework coverage</TableHead>
              <TableHead>Last attested</TableHead>
              <TableHead>Next due</TableHead>
              <TableHead>Score vs risk bar</TableHead>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <SupplierTableRow
                key={row.id}
                row={row}
                selected={selectedId === row.id}
                onClick={() => setSelectedId(row.id === selectedId ? null : row.id)}
              />
            ))}
          </tbody>
        </table>
        {filtered.length === 0 ? (
          <div
            style={{
              padding: 'var(--space-6)',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            No suppliers match this filter.
          </div>
        ) : null}
      </div>

      {/* Detail drawer */}
      {selected ? <SupplierDetail row={selected} onClose={() => setSelectedId(null)} /> : null}
    </SampleShell>
  );
}

function SummaryTile({
  label,
  value,
  caption,
  accent,
  alert,
}: {
  label: string;
  value: number;
  caption: string;
  accent?: boolean;
  alert?: boolean;
}) {
  const borderColor = alert
    ? 'var(--band-red-mark)'
    : accent
      ? 'var(--buying)'
      : 'var(--border)';
  const valueColor = alert ? 'var(--band-red)' : 'var(--text)';
  return (
    <div
      style={{
        padding: 'var(--space-4)',
        border: `1px solid ${borderColor}`,
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: 'var(--text-dim)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 'clamp(1.75rem, 3vw, 2.25rem)',
          fontWeight: 700,
          color: valueColor,
          lineHeight: 1,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        {caption}
      </div>
    </div>
  );
}

function FilterChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        border: `1px solid ${active ? 'var(--buying)' : 'var(--border-strong)'}`,
        borderRadius: '999px',
        background: active ? 'var(--buying-soft)' : 'var(--surface)',
        color: active ? 'var(--buying)' : 'var(--text-muted)',
        fontFamily: 'var(--font-ui)',
        fontSize: 'var(--font-size-2xs)',
        fontWeight: active ? 700 : 500,
        cursor: 'pointer',
      }}
    >
      {label}
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          padding: '0 var(--space-1)',
          borderRadius: '999px',
          background: active ? 'var(--buying)' : 'var(--surface-elevated)',
          color: active ? 'var(--text-inverse)' : 'var(--text-dim)',
        }}
      >
        {count}
      </span>
    </button>
  );
}

function TableHead({ children }: { children: React.ReactNode }) {
  return (
    <th
      style={{
        textAlign: 'left',
        padding: 'var(--space-3)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        fontWeight: 500,
        borderBottom: '1px solid var(--border)',
      }}
    >
      {children}
    </th>
  );
}

function SupplierTableRow({
  row,
  selected,
  onClick,
}: {
  row: SupplierRow;
  selected: boolean;
  onClick: () => void;
}) {
  const nextDueDays = daysBetween(row.nextAttestationDue);
  const dueLabel =
    nextDueDays === null
      ? '—'
      : nextDueDays < 0
        ? `${Math.abs(nextDueDays)}d overdue`
        : `${nextDueDays}d`;
  const dueColor = nextDueDays !== null && nextDueDays < 0 ? 'var(--band-red)' : 'var(--text-muted)';

  return (
    <tr
      onClick={onClick}
      style={{
        borderTop: '1px solid var(--border)',
        cursor: 'pointer',
        background: selected ? 'var(--buying-soft)' : 'transparent',
      }}
    >
      <td style={{ padding: 'var(--space-3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <div>
            <div style={{ fontWeight: 600, color: 'var(--text)' }}>{row.name}</div>
            <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
              {row.category}
            </div>
          </div>
          {row.undisclosedAi ? (
            <span
              title="Undisclosed AI detected in identity logs"
              style={{
                padding: '2px var(--space-2)',
                borderRadius: '999px',
                background: 'var(--band-red-bg)',
                color: 'var(--band-red)',
                fontFamily: 'var(--font-mono)',
                fontSize: 'var(--font-size-3xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
                fontWeight: 700,
              }}
            >
              Undisclosed AI
            </span>
          ) : null}
        </div>
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        <DispositionChip
          label={DISPOSITION_LABELS[row.disposition]}
          tone={toneForDisposition(row.disposition)}
        />
      </td>
      <td style={{ padding: 'var(--space-3)', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
        {row.riskTier}
      </td>
      <td style={{ padding: 'var(--space-3)', color: 'var(--text-muted)' }}>{row.aiSystemsCount}</td>
      <td style={{ padding: 'var(--space-3)' }}>
        <FrameworkCoverageBar pct={row.frameworkCoveragePct} />
      </td>
      <td style={{ padding: 'var(--space-3)', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)' }}>
        {fmtDate(row.lastAttested)}
      </td>
      <td style={{ padding: 'var(--space-3)', color: dueColor, fontFamily: 'var(--font-mono)', fontSize: 'var(--font-size-3xs)' }}>
        {dueLabel}
      </td>
      <td style={{ padding: 'var(--space-3)' }}>
        <ScoreBadge score={row.score} />
      </td>
    </tr>
  );
}

function FrameworkCoverageBar({ pct }: { pct: number }) {
  const clamped = Math.max(0, Math.min(100, pct));
  const barColor =
    clamped >= 80 ? 'var(--band-green-mark)' : clamped >= 50 ? 'var(--band-amber-mark)' : 'var(--band-red-mark)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <div
        style={{
          width: '64px',
          height: '6px',
          borderRadius: '999px',
          background: 'var(--surface-inset)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            width: `${clamped}%`,
            height: '100%',
            background: barColor,
          }}
        />
      </div>
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
        }}
      >
        {clamped}%
      </span>
    </div>
  );
}

function ScoreBadge({ score }: { score: number }) {
  const color =
    score >= 80 ? 'var(--band-green)' : score >= 50 ? 'var(--band-amber)' : 'var(--band-red)';
  const bg =
    score >= 80 ? 'var(--band-green-bg)' : score >= 50 ? 'var(--band-amber-bg)' : 'var(--band-red-bg)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-card)',
        background: bg,
        color,
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-2xs)',
        fontWeight: 700,
      }}
    >
      {score}
    </span>
  );
}

function SupplierDetail({ row, onClose }: { row: SupplierRow; onClose: () => void }) {
  return (
    <div
      role="dialog"
      aria-label={`Detail for ${row.name}`}
      style={{
        marginTop: 'var(--space-4)',
        border: `1px solid var(--buying)`,
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-6)',
        display: 'grid',
        gap: 'var(--space-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-3xs)',
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--buying)',
              fontWeight: 700,
            }}
          >
            SUPPLIER · {row.category.toUpperCase()}
          </div>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-lg)', color: 'var(--text)' }}>
            {row.name}
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: 'none',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-card)',
            color: 'var(--text-muted)',
            padding: 'var(--space-1) var(--space-3)',
            fontFamily: 'var(--font-ui)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
          }}
        >
          Close
        </button>
      </div>

      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text-muted)',
          lineHeight: 'var(--line-height-body)',
        }}
      >
        {row.narrative}
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        <MiniStat label="Disposition" value={DISPOSITION_LABELS[row.disposition]} />
        <MiniStat label="Risk tier" value={row.riskTier} capitalize />
        <MiniStat label="AI systems in-tenant" value={String(row.aiSystemsCount)} />
        <MiniStat label="Framework coverage" value={`${row.frameworkCoveragePct}%`} />
        <MiniStat label="Score vs risk bar" value={String(row.score)} />
        <MiniStat label="Last attested" value={fmtDate(row.lastAttested)} />
        <MiniStat label="Next due" value={fmtDate(row.nextAttestationDue)} />
      </div>

      {/* Signals timeline */}
      <div>
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-dim)',
            marginBottom: 'var(--space-2)',
            fontWeight: 600,
          }}
        >
          Attestation signals
        </div>
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          {row.signals.map((s, i) => (
            <div
              key={i}
              style={{
                display: 'flex',
                gap: 'var(--space-3)',
                padding: 'var(--space-2) var(--space-3)',
                background: 'var(--surface-elevated)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-card)',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--font-size-xs)',
                alignItems: 'baseline',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--font-size-3xs)',
                  color: 'var(--text-dim)',
                  whiteSpace: 'nowrap',
                }}
              >
                {fmtDate(s.observed)}
              </span>
              <span style={{ color: 'var(--text)' }}>{s.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Named signer */}
      <div
        style={{
          padding: 'var(--space-3)',
          border: '1px dashed var(--border-strong)',
          borderRadius: 'var(--radius-card)',
        }}
      >
        <div
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--font-size-3xs)',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: 'var(--text-dim)',
            marginBottom: 'var(--space-2)',
            fontWeight: 600,
          }}
        >
          Named signer at supplier (Layer 2 — source product owner)
        </div>
        {row.namedSigner ? (
          <div style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
            <strong>{row.namedSigner.displayName}</strong>
            <span style={{ color: 'var(--text-muted)' }}>
              {' '}
              · {row.namedSigner.role}, {row.namedSigner.company}
            </span>
          </div>
        ) : (
          <div style={{ fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            No signer on file. Attestation request escalated to remediation SLA.
          </div>
        )}
      </div>
    </div>
  );
}

function MiniStat({ label, value, capitalize }: { label: string; value: string; capitalize?: boolean }) {
  return (
    <div style={{ display: 'grid', gap: '2px' }}>
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--font-size-3xs)',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          color: 'var(--text-dim)',
          fontWeight: 600,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--text)',
          fontWeight: 500,
          textTransform: capitalize ? 'capitalize' : 'none',
        }}
      >
        {value}
      </div>
    </div>
  );
}
